<template>
  <transition
    :name="ns.b('fade')"
    @before-leave="onClose"
    @after-leave="$emit('destroy')"
  >
    <div
      v-show="visible"
      :id="id"
      :class="[ns.b(), customClass, horizontalClass]"
      :style="positionStyle"
      role="alert"
      @mouseenter="clearTimer"
      @mouseleave="startTimer"
      @click="onClick"
    >
      <div :class="ns.e('container')">
        <jd-icon v-if="iconComponent" :class="[ns.e('icon'), typeClass]">
          <component :is="iconComponent" />
        </jd-icon>
        <div :class="ns.e('group')">
          <h2 :class="ns.e('title')" v-text="title" />
          <div
            v-show="message"
            :class="ns.e('content')"
            :style="!!title ? undefined : { margin: 0 }"
          >
            <slot>
              <p v-if="!dangerouslyUseHTMLString">{{ message }}</p>
              <!-- Caution here, message could've been compromised, never use user's input as message -->
              <p v-else v-html="message" />
            </slot>
          </div>
          <jd-icon v-if="showClose" :class="ns.e('closeBtn')" @click.stop="close">
            <Close />
          </jd-icon>
        </div>
      </div>
      <div v-if="showConfirmButton || showCancelButton" :class="ns.e('btns')">
        <jd-button
          v-if="showConfirmButton"
          ref="confirmRef"
          type="primary"
          :disabled="confirmButtonDisabled"
          :size="btnSize"
          @click.stop="confirm"
        >
          {{ confirmButtonText || t('el.notification.confirm') }}
        </jd-button>
        <jd-button
          v-if="showCancelButton"
          :size="btnSize"
          plain
          @click.stop="close"
        >
          {{ cancelButtonText || t('el.notification.cancel') }}
        </jd-button>
      </div>
    </div>
  </transition>
</template>
<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue'
import { useEventListener, useTimeoutFn } from '@vueuse/core'
import { CloseComponents, TypeComponentsMap } from '@jdesign-vue/utils'
import { EVENT_CODE } from '@jdesign-vue/constants'
import { JdIcon } from '@jdesign-vue/components/icon'
import { useGlobalComponentSettings } from '@jdesign-vue/components/config-provider'
import { notificationEmits, notificationProps } from './notification'
import type { Action } from './notification.type'

import type { CSSProperties } from 'vue'

defineOptions({
  name: 'JdNotification',
})

const props = defineProps(notificationProps)
defineEmits(notificationEmits)

const {
  ns,
  zIndex,
  locale,
  size: btnSize,
} = useGlobalComponentSettings('notification')
const { nextZIndex, currentZIndex } = zIndex
const { t } = locale

const { Close } = CloseComponents

const visible = ref(false)
const actionType = ref<Action>('close')
let timer: (() => void) | undefined = undefined

const typeClass = computed(() => {
  const type = props.type
  return type && TypeComponentsMap[props.type] ? ns.m(type) : ''
})

const iconComponent = computed(() => {
  if (!props.type) return props.icon
  return TypeComponentsMap[props.type] || props.icon
})

const horizontalClass = computed(() =>
  props.position.endsWith('right') ? 'right' : 'left'
)

const verticalProperty = computed(() =>
  props.position.startsWith('top') ? 'top' : 'bottom'
)

const positionStyle = computed<CSSProperties>(() => {
  return {
    [verticalProperty.value]: `${props.offset}px`,
    zIndex: props.zIndex ?? currentZIndex.value,
  }
})

function startTimer() {
  if (props.duration > 0) {
    ;({ stop: timer } = useTimeoutFn(() => {
      if (visible.value) close()
    }, props.duration))
  }
}

function clearTimer() {
  timer?.()
}

function close() {
  actionType.value = 'close'
  visible.value = false
}

function confirm() {
  actionType.value = 'confirm'
  visible.value = false
}

function onKeydown({ code }: KeyboardEvent) {
  if (code === EVENT_CODE.delete || code === EVENT_CODE.backspace) {
    clearTimer() // press delete/backspace clear timer
  } else if (code === EVENT_CODE.esc) {
    // press esc to close the notification
    if (visible.value) {
      close()
    }
  } else {
    startTimer() // resume timer
  }
}

function onClose() {
  if (actionType.value === 'confirm') {
    props.onConfirm?.()
  } else {
    props.onClose?.()
  }
}

// lifecycle
onMounted(() => {
  startTimer()
  nextZIndex()
  visible.value = true
})

useEventListener(document, 'keydown', onKeydown)

defineExpose({
  visible,
  /** @description close notification */
  close,
  /** @description confirm notification */
  confirm,
})
</script>
